import React, { useState, useEffect } from 'react';
import { Html } from '@react-three/drei';

interface ContentSurfaceProps {
    mediaMode?: 'radio' | 'video' | 'zen';
    youtubeId?: string;
    soundcloudUrl?: string;
}

export function ContentSurface({ mediaMode, youtubeId = "jfKfPfyJRdk", soundcloudUrl }: ContentSurfaceProps) {
    const [activeTab, setActiveTab] = useState('radio'); // radio, video, zen

    // Auto-switch tab based on Environment Mode
    useEffect(() => {
        if (mediaMode) {
            setActiveTab(mediaMode);
        }
    }, [mediaMode]);

    const styles = {
        container: {
            width: '800px',
            height: '500px',
            background: 'rgba(0, 5, 16, 0.85)',
            border: '2px solid #00f3ff',
            borderRadius: '10px',
            padding: '20px',
            color: '#00f3ff',
            fontFamily: 'Courier New',
            display: 'flex',
            flexDirection: 'column' as const,
            boxShadow: '0 0 40px rgba(0, 243, 255, 0.2)',
            transform: 'scale(0.5)', // Scale down HTML to fit nicely in 3D
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '20px',
            borderBottom: '1px solid #333',
            paddingBottom: '10px'
        },
        tab: (isActive: boolean) => ({
            padding: '10px 20px',
            cursor: 'pointer',
            borderBottom: isActive ? '2px solid #00f3ff' : 'none',
            color: isActive ? '#00f3ff' : '#555',
            fontWeight: isActive ? 'bold' : 'normal',
            transition: 'color 0.3s'
        }),
        content: {
            flex: 1,
            background: '#000',
            borderRadius: '5px',
            overflow: 'hidden',
            position: 'relative' as const
        }
    };

    const scUrl = soundcloudUrl || "https://api.soundcloud.com/playlists/1732126665";

    return (
        <group position={[3.5, 1.5, -2]} rotation={[0, -0.5, 0]}>
            <Html transform occlude>
                <div style={styles.container}>
                    {/* Header / Tabs */}
                    <div style={styles.header}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>GNI MEDIA DECK</div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={styles.tab(activeTab === 'radio')} onClick={() => setActiveTab('radio')}>RADIO</div>
                            <div style={styles.tab(activeTab === 'video')} onClick={() => setActiveTab('video')}>VIDEO</div>
                            <div style={styles.tab(activeTab === 'zen')} onClick={() => setActiveTab('zen')}>ZEN</div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div style={styles.content}>
                        {activeTab === 'radio' && (
                            <iframe
                                width="100%"
                                height="100%"
                                scrolling="no"
                                frameBorder="no"
                                allow="autoplay"
                                src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(scUrl)}&color=%2300f3ff&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
                            ></iframe>
                        )}

                        {activeTab === 'video' && (
                            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#333' }}>
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${youtubeId}`}
                                    title="YouTube Video"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        )}

                        {activeTab === 'zen' && (
                            <img
                                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }}
                            />
                        )}
                    </div>

                    <div style={{ marginTop: '10px', fontSize: '10px', color: '#555', textAlign: 'right' }}>
                        SIGNAL STRENGTH: 100% | ENCRYPTION: GNI-256
                    </div>
                </div>
            </Html>
        </group>
    );
}
